import { excursionsCounterLogic } from './versions/excursionsWithinTabs';
import { checkIfScrollable, getRetargetingItineraryCodes, keyCodes } from '@msc-b2c/shared-utils';
import { flushPendingCarousels } from '../carousel/carousel';

declare global {
  interface Window {
    Granite?: any;
    [key: string]: any;
    didomiOnReady?: any[];
  }
  interface HTMLElement {
    dataset: DOMStringMap & { cmpDataLayer?: string };
  }
}

type TabsOptions = Record<string, any>;

interface TabsConfig {
  element: HTMLElement;
  options: TabsOptions;
}

const containerUtils = window.CQ?.CoreComponents?.container?.utils ?? undefined;

if (!containerUtils) {
  console.warn(
    'Tabs: container utilities at window.CQ.CoreComponents.container.utils are not available. This can lead to missing features. Ensure the core.wcm.components.commons.site.container client library is included on the page.'
  );
}

let dataLayerEnabled: boolean;
let dataLayer: any[];
let dataLayerName: string;

const NS = 'cmp';
const IS = 'tabs';

//for hiding the end blur effect in sailing tabs
function updateSailingTabsFade(tab: HTMLElement): void {
  const wrapper = tab.classList.contains('cmp-tabs')
    ? tab.querySelector<HTMLElement>('.tabsWrapper')
    : tab.querySelector<HTMLElement>('.cmp-tabs .tabsWrapper');

  const tabList = wrapper?.querySelector<HTMLElement>('.cmp-tabs__tablist');

  if (!wrapper || !tabList) return;

  const isAtEnd = tabList.scrollLeft + tabList.clientWidth >= tabList.scrollWidth - 1;

  wrapper.classList.toggle('is-at-scroll-end', isAtEnd);
}

// Checks if this tabs component is the Sailing carousel tabs variation.
function isSailingCarouselTabs(wrapper: HTMLElement): boolean {
  return (
    wrapper.dataset['sailingCarouselTabs'] === 'true' ||
    wrapper.dataset['cmpSailingCarouselTabs'] === 'true' ||
    wrapper.getAttribute('data-sailing-carousel-tabs') === 'true' ||
    wrapper.getAttribute('data-cmp-sailing-carousel-tabs') === 'true' ||
    wrapper.classList.contains('cmp-tabs--sailing-carousel') ||
    Boolean(wrapper.closest('.cmp-tabs--sailing-carousel'))
  );
}

// Checks local storage for visited itineraries.
function hasRetargetingVisits(): boolean {
  return getRetargetingItineraryCodes().length > 0;
}

// Marks tabs hidden by the retargeting logic.
function isHiddenRetargetingTab(tab: HTMLElement): boolean {
  return tab.dataset['retargetingHidden'] === 'true';
}

const selectors = {
  self: `[data-${NS}-is="${IS}"], .cmp-tabs[data-sailing-carousel-tabs="true"], .cmp-tabs[data-cmp-sailing-carousel-tabs="true"]`,
  active: {
    tab: 'cmp-tabs__tab--active',
    tabpanel: 'cmp-tabs__tabpanel--active',
  },
};

export function tabHandler(): void {
  initializeTabs();
  const tabs = document.querySelectorAll('.tabs') ?? [];
  if (tabs.length === 0) return;

  const tabId = window.location.hash;

  if (tabId) {
    const tab = document.querySelector<HTMLElement>(tabId);
    tab?.click();
  }

  // when we have just on tab lets disable pointer events
  const disableEventsOnSingleTab = (tab: HTMLElement) => {
    const tabList = tab.querySelectorAll('.cmp-tabs > .cmp-tabs__tablist > li');
    if (tabList && tabList.length === 1) {
      const activeTab = tabList[0] as HTMLElement;
      activeTab.style.pointerEvents = 'none';
    }
  };

  const updateTabCounts = (tab: HTMLElement) => {
    const tabList = tab.querySelectorAll('.cmp-tabs > .cmp-tabs__tablist > .cmp-tabs__tab');
    tabList.forEach((tabItem) => {
      const tabElement = tabItem as HTMLElement;
      const tabId = tabElement.getAttribute('data-tab');
      if (tabId) {
        const tabContent = tab.querySelector(`[data-tab-content="${tabId}"]`);
        if (tabContent) {
          const allTeasers = tabContent.querySelectorAll('.gallery-teaser');
          const currentText = tabElement.textContent?.replace(/\(\d+\)/, '').trim() || '';
          tabElement.innerHTML = `(${allTeasers.length}) ${currentText}`;
        }
      }
    });
  };
  tabs.forEach((tab) => {
    updateTabCounts(tab as HTMLElement);
    disableEventsOnSingleTab(tab as HTMLElement);
    if (tab.classList.contains('cmp-tabs__excursions-counter')) {
      excursionsCounterLogic(tab as HTMLElement);
    }
    checkIfScrollable(tab);
    updateSailingTabsFade(tab as HTMLElement);

    // Reserve space for the bold active state so font-weight change does not shift sibling tabs.
    // Must run after updateTabCounts so data-tab-text captures the final label text.
    (tab as HTMLElement).querySelectorAll<HTMLElement>('.cmp-tabs__tab').forEach((el) => {
      const text = el.textContent?.trim();
      if (!text) return;
      if (text.includes('(replace-departure-count)') || text.includes('(replace-visiting-count)')) {
        return;
      }
      el.dataset['tabText'] = text;
    });
    const tabList = (tab as HTMLElement).querySelector<HTMLElement>('.cmp-tabs .cmp-tabs__tablist');

    if (tabList) {
      tabList.addEventListener('scroll', () => updateSailingTabsFade(tab as HTMLElement));

      window.requestAnimationFrame(() => updateSailingTabsFade(tab as HTMLElement));

      window.addEventListener('resize', () => updateSailingTabsFade(tab as HTMLElement));
    }

    tab.querySelectorAll('.cmp-tabs__tab').forEach((element) =>
      element.addEventListener('click', () => {
        if (isHiddenRetargetingTab(element as HTMLElement)) return;

        const id = element.id;
        const tabPanel = tab.querySelector('#' + id.replace('-tab', '-tabpanel'));
        const childTab: Element | null | undefined = tabPanel?.querySelector('.tabs');
        if (!id || !tabPanel || !childTab) return;
        checkIfScrollable(childTab);
      })
    );
  });
}

class Tabs {
  private _elements: Record<string, any> = {};
  private _active = 0;
  private noTabFocus = false;
  private isSailingCarouselTabs = false;
  private shouldHideRetargetingTab = false;

  constructor(config: TabsConfig) {
    if (config?.element) {
      this.init(config);
    }
  }

  private init(config: TabsConfig): void {
    config.element.removeAttribute(`data-${NS}-is`);
    config.element.removeAttribute('data-is');

    this.cacheElements(config.element);
    // Remove old init marker if the component uses data-is instead of data-cmp-is.
    this.isSailingCarouselTabs = isSailingCarouselTabs(this._elements['self']);
    this._active = this.getActiveIndex(this._elements['tab']);
    this.configureSailingCarouselRetargeting();

    if (
      this._elements['tab']?.[this._active] &&
      isHiddenRetargetingTab(this._elements['tab'][this._active])
    ) {
      this._active = this.getFirstVisibleIndex();
    }

    // check if we are in port hub page
    // if yes we dont want element.focus()
    const portHubMainTab = document.querySelector('.cmp-portsgallerytabs');
    this.noTabFocus = portHubMainTab ? true : false;

    if (this._elements['tabpanel']) {
      this.refreshActive();
      this.bindEvents();
      this.scrollToDeepLinkIdInTabs();
    }

    if (window.Granite?.author?.MessageChannel && window.CQ?.CoreComponents) {
      window.CQ.CoreComponents.MESSAGE_CHANNEL =
        window.CQ.CoreComponents.MESSAGE_CHANNEL ||
        new window.Granite.author.MessageChannel('cqauthor', window);

      window.CQ.CoreComponents.MESSAGE_CHANNEL.subscribeRequestMessage(
        'cmp.panelcontainer',
        (message: { data: { type: string; id: string; operation: string; index: number } }) => {
          if (
            message.data?.type === 'cmp-tabs' &&
            message.data.id === this._elements['self'].dataset['cmpPanelcontainerId']
          ) {
            if (message.data.operation === 'navigate') {
              this.navigate(message.data.index);
            }
          }
        }
      );
    }
  }

  // Hide or show the retargeting tab before active state is refreshed.
  private getFirstVisibleIndex(): number {
    const tabs = this._elements['tab'];

    if (tabs) {
      for (let i = 0; i < tabs.length; i++) {
        if (!isHiddenRetargetingTab(tabs[i])) {
          return i;
        }
      }
    }

    return 0;
  }

  private scrollToDeepLinkIdInTabs(): void {
    if (containerUtils?.getDeepLinkItemIdx) {
      const deepLinkItemIdx = containerUtils.getDeepLinkItemIdx(this, 'tab', 'tabpanel');
      if (deepLinkItemIdx > -1) {
        const deepLinkItem = this._elements['tab'][deepLinkItemIdx];
        if (
          deepLinkItem &&
          !isHiddenRetargetingTab(deepLinkItem) &&
          this._elements['tab'][this._active].id !== deepLinkItem.id
        ) {
          this.navigateAndFocusTab(deepLinkItemIdx);
        }
        const hashId = window.location.hash.substring(1);
        if (hashId) {
          const hashItem = document.querySelector<HTMLElement>(`[id='${hashId}']`);
          hashItem?.scrollIntoView();
        }
      }
    }
  }

  // Avoid navigating to a hidden retargeting tab from the URL hash.
  private configureSailingCarouselRetargeting(): void {
    if (!this.isSailingCarouselTabs) return;

    const tabs = this._elements['tab'];
    const tabpanels = this._elements['tabpanel'];

    if (!Array.isArray(tabs) || !Array.isArray(tabpanels) || tabs.length < 2) return;

    // For Sailing carousel tabs, the first tab is reserved for retargeting.
    // If there are no visited itineraries in local storage, hide the first tab and its panel.
    this.shouldHideRetargetingTab = !hasRetargetingVisits();

    const retargetingTab = tabs[0] as HTMLElement;
    const retargetingPanel = tabpanels[0] as HTMLElement;

    if (this.shouldHideRetargetingTab) {
      retargetingTab.dataset['retargetingHidden'] = 'true';
      retargetingTab.setAttribute('hidden', 'true');
      retargetingTab.setAttribute('aria-hidden', 'true');
      retargetingTab.setAttribute('aria-selected', 'false');
      retargetingTab.setAttribute('tabindex', '-1');
      retargetingTab.style.display = 'none';

      retargetingPanel.dataset['retargetingHidden'] = 'true';
      retargetingPanel.classList.remove(selectors.active.tabpanel);
      retargetingPanel.setAttribute('hidden', 'true');
      retargetingPanel.setAttribute('aria-hidden', 'true');
      retargetingPanel.style.display = 'none';

      this._active = this.getFirstVisibleIndex();
    } else {
      retargetingTab.removeAttribute('data-retargeting-hidden');
      retargetingTab.removeAttribute('hidden');
      retargetingTab.removeAttribute('aria-hidden');
      retargetingTab.style.display = '';

      retargetingPanel.removeAttribute('data-retargeting-hidden');
      retargetingPanel.removeAttribute('hidden');
      retargetingPanel.removeAttribute('aria-hidden');
      retargetingPanel.style.display = '';

      this._active = 0;
    }
  }

  private getActiveIndex(tabs: HTMLElement[]): number {
    if (tabs) {
      for (let i = 0; i < tabs.length; i++) {
        if (isHiddenRetargetingTab(tabs[i])) continue;

        if (tabs[i].classList.contains(selectors.active.tab)) {
          return i;
        }
      }

      for (let i = 0; i < tabs.length; i++) {
        if (!isHiddenRetargetingTab(tabs[i])) {
          return i;
        }
      }
    }
    return 0;
  }

  private cacheElements(wrapper: HTMLElement): void {
    this._elements = {};
    this._elements['self'] = wrapper;
    const hooks = this._elements['self'].querySelectorAll(`[data-${NS}-hook-${IS}]`);

    for (const element of hooks) {
      const hook = element as HTMLElement;
      if (hook.closest(`.${NS}-${IS}`) === this._elements['self']) {
        const capitalized = IS.charAt(0).toUpperCase() + IS.slice(1);
        const key = hook.dataset[NS + 'Hook' + capitalized]!;
        if (this._elements[key]) {
          if (!Array.isArray(this._elements[key])) {
            this._elements[key] = [this._elements[key]];
          }
          this._elements[key].push(hook);
        } else {
          this._elements[key] = hook;
        }
      }
    }
  }

  private bindEvents(): void {
    window.addEventListener('hashchange', () => this.scrollToDeepLinkIdInTabs());
    const tabs = this._elements['tab'];
    if (tabs) {
      for (let i = 0; i < tabs.length; i++) {
        tabs[i].addEventListener('click', () => {
          // Skip hidden retargeting tab when finding the active tab.
          if (isHiddenRetargetingTab(tabs[i])) return;

          this.navigateAndFocusTab(i);
          const tabEl = tabs[i] as HTMLElement;
          const tablist = tabEl.parentElement as HTMLElement | null;

          if (tablist && tablist.classList.contains('cmp-tabs__tablist')) {
            tablist.scrollTo({
              left: tabEl.offsetLeft,
              behavior: 'smooth',
            });
          }
        });
        tabs[i].addEventListener('keydown', (event: KeyboardEvent) => {
          this.onKeyDown(event);
        });
      }
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    const index = this._active;
    const lastIndex = this._elements['tab'].length - 1;

    const getNextVisibleIndex = (start: number, direction: 1 | -1): number => {
      let nextIndex = start + direction;

      while (nextIndex >= 0 && nextIndex <= lastIndex) {
        if (!isHiddenRetargetingTab(this._elements['tab'][nextIndex])) {
          return nextIndex;
        }

        nextIndex += direction;
      }

      return start;
    };

    switch (event.key) {
      case keyCodes.ARROW_LEFT:
      case keyCodes.ARROW_UP:
        event.preventDefault();
        if (index > 0) this.navigateAndFocusTab(getNextVisibleIndex(index, -1));
        break;
      case keyCodes.ARROW_RIGHT:
      case keyCodes.ARROW_DOWN:
        event.preventDefault();
        if (index < lastIndex) this.navigateAndFocusTab(getNextVisibleIndex(index, 1));
        break;
      case keyCodes.HOME:
        event.preventDefault();
        this.navigateAndFocusTab(this.getFirstVisibleIndex());
        break;
      case keyCodes.END:
        event.preventDefault();
        for (let i = lastIndex; i >= 0; i--) {
          if (!isHiddenRetargetingTab(this._elements['tab'][i])) {
            this.navigateAndFocusTab(i);
            break;
          }
        }
        break;
    }
  }

  private refreshActive(): void {
    const tabpanels = this._elements['tabpanel'];
    const tabs = this._elements['tab'];

    if (tabs && tabpanels) {
      if (Array.isArray(tabpanels)) {
        if (!tabs[this._active] || isHiddenRetargetingTab(tabs[this._active])) {
          this._active = this.getFirstVisibleIndex();
        }

        for (let i = 0; i < tabpanels.length; i++) {
          if (isHiddenRetargetingTab(tabs[i])) {
            tabpanels[i].classList.remove(selectors.active.tabpanel);
            tabpanels[i].setAttribute('hidden', 'true');
            tabpanels[i].setAttribute('aria-hidden', 'true');
            tabpanels[i].style.display = 'none';

            tabs[i].classList.remove(selectors.active.tab);
            tabs[i].setAttribute('hidden', 'true');
            tabs[i].setAttribute('aria-hidden', 'true');
            tabs[i].setAttribute('aria-selected', 'false');
            tabs[i].setAttribute('tabindex', '-1');
            tabs[i].style.display = 'none';
            continue;
          }

          tabs[i].removeAttribute('hidden');
          tabs[i].removeAttribute('aria-hidden');
          tabs[i].style.display = '';

          tabpanels[i].style.display = '';

          if (i === this._active) {
            tabpanels[i].classList.add(selectors.active.tabpanel);
            tabpanels[i].removeAttribute('hidden');
            tabpanels[i].removeAttribute('aria-hidden');
            tabpanels[i].removeAttribute('inert');
            tabs[i].classList.add(selectors.active.tab);
            tabs[i].setAttribute('aria-selected', 'true');
            tabs[i].setAttribute('tabindex', '0');
          } else {
            tabpanels[i].classList.remove(selectors.active.tabpanel);
            tabpanels[i].setAttribute('hidden', 'true');
            tabpanels[i].setAttribute('aria-hidden', 'true');
            tabpanels[i].setAttribute('inert', '');
            tabs[i].classList.remove(selectors.active.tab);
            tabs[i].setAttribute('aria-selected', 'false');
            tabs[i].setAttribute('tabindex', '-1');
          }
        }
      } else {
        tabpanels.classList.add(selectors.active.tabpanel);
        tabs.classList.add(selectors.active.tab);
      }

      // Inactive panels are hidden, so their carousels never enter the viewport.
      const activePanel = Array.isArray(tabpanels) ? tabpanels[this._active] : tabpanels;
      if (activePanel) {
        flushPendingCarousels(activePanel);
      }
    }
  }

  private focusWithoutScroll(element: HTMLElement): void {
    const x = window.scrollX || window.pageXOffset;
    const y = window.scrollY || window.pageYOffset;
    this.noTabFocus ? '' : element.focus();
    window.scrollTo(x, y);
  }

  private navigate(index: number): void {
    if (!this._elements['tab']?.[index] || isHiddenRetargetingTab(this._elements['tab'][index])) {
      this._active = this.getFirstVisibleIndex();
    } else {
      this._active = index;
    }

    this.refreshActive();
  }

  private navigateAndFocusTab(index: number): void {
    const exActive = this._active;
    this.navigate(index);
    this.focusWithoutScroll(this._elements['tab'][this._active]);

    if (dataLayerEnabled && this._elements['tabpanel']) {
      const activeItem = getDataLayerId(this._elements['tabpanel'][this._active]);
      const exActiveItem = getDataLayerId(this._elements['tabpanel'][exActive]);
      window.didomiOnReady = window.didomiOnReady || [];
      console.log('Didomi Wrapper tab cmp:show/hide - About to push callback');
      window.didomiOnReady.push(() => {
        console.log('Didomi Wrapper tab cmp:show/hide called - Didomi is ready');
        console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
        dataLayer.push({
          event: 'cmp:show',
          eventInfo: { path: 'component.' + activeItem },
        });

        dataLayer.push({
          event: 'cmp:hide',
          eventInfo: { path: 'component.' + exActiveItem },
        });
        console.log('Custom event tab cmp:show/hide pushed into the data layer');
      });
      const tabsId = this._elements['self'].id;
      const uploadPayload: any = { component: {} };
      uploadPayload.component[tabsId] = { shownItems: [activeItem] };

      const removePayload: any = { component: {} };
      removePayload.component[tabsId] = { shownItems: undefined };

      window.didomiOnReady = window.didomiOnReady || [];
      console.log('Didomi Wrapper tab dataLayer update - About to push callback');
      window.didomiOnReady.push(() => {
        console.log('Didomi Wrapper tab dataLayer update called - Didomi is ready');
        console.log('didomiVendorsEnabled =', window.didomiState?.didomiVendorsEnabled);
        console.log('Data to be pushed (removePayload):', removePayload);
        dataLayer.push(removePayload);
        console.log('Data to be pushed (uploadPayload):', uploadPayload);
        dataLayer.push(uploadPayload);
        console.log('Custom event tab dataLayer update pushed into the data layer');
      });
    }
  }
}
function readData(element: HTMLElement): TabsOptions {
  const data = element.dataset;
  const options: TabsOptions = {};
  const capitalized = IS.charAt(0).toUpperCase() + IS.slice(1);
  const reserved = ['is', 'hook' + capitalized];

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value: string | undefined = data[key];
      if (key.startsWith(NS)) {
        let optionKey = key.slice(NS.length);
        optionKey = optionKey.charAt(0).toLowerCase() + optionKey.substring(1);
        if (!reserved.includes(optionKey)) {
          options[optionKey] = value;
        }
      }
    }
  }
  return options;
}

function getDataLayerId(item: HTMLElement): string | null {
  if (item) {
    if (item.dataset['cmpDataLayer']) {
      return Object.keys(JSON.parse(item.dataset['cmpDataLayer']))[0];
    } else {
      return item.id;
    }
  }
  return null;
}

function initializeTabs(): void {
  dataLayerEnabled = document.body.hasAttribute('data-cmp-data-layer-enabled');
  if (dataLayerEnabled) {
    dataLayerName = document.body.getAttribute('data-cmp-data-layer-name') || 'adobeDataLayer';
    dataLayer = window[dataLayerName] = window[dataLayerName] || [];
  }

  const elements = document.querySelectorAll<HTMLElement>(selectors.self);
  elements.forEach((el) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _tabsObj = new Tabs({ element: el, options: readData(el) });
  });

  const MutationObserver =
    window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;

  const body = document.querySelector('body');
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const nodesArray = Array.from(mutation.addedNodes);
      nodesArray.forEach((addedNode: any) => {
        if (addedNode.querySelectorAll) {
          const elementsArray = Array.from(addedNode.querySelectorAll(selectors.self));
          elementsArray.forEach((element) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _tabsObj = new Tabs({
              element: element as HTMLElement,
              options: readData(element as HTMLElement),
            });
          });
        }
      });
    });
  });

  if (body)
    observer.observe(body, {
      subtree: true,
      childList: true,
      characterData: true,
    });
}

if (document.readyState !== 'loading') {
  tabHandler();
} else {
  document.addEventListener('DOMContentLoaded', tabHandler);
}
